// =============================================================================
// GiftCardView — one physical gift card: status, balance, expiry, history and
// the staff actions. Shared by the detail page (/cadeaubon/:code) and the
// Scan screen's GiftCardModal.
//   * booking_gift_card_sell:   Activeren / Waarde toevoegen (WisePOS)
//   * booking_gift_card_manage: block / unblock, balance correction
// =============================================================================
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatError } from '../lib/errors';
import { formatVenueDate, formatVenueDateTime } from '../lib/time';
import {
  adjustGiftCard,
  formatEuro,
  giftCardStatusOf,
  setGiftCardBlocked,
  TX_KIND_LABEL,
  type GiftCardDetail,
  type GiftCardLoadStatus,
} from '../lib/giftcard';
import { AuditTimeline } from './AuditTimeline';

const LOAD_STATUS_LABEL: Record<GiftCardLoadStatus, string> = {
  pending:   'Bezig',
  succeeded: 'Betaald',
  failed:    'Mislukt',
  canceled:  'Afgebroken',
  refunded:  'Terugbetaald',
};

export function GiftCardView({
  detail,
  onChanged,
  loadAction,
  compact = false,
}: {
  detail: GiftCardDetail;
  onChanged: () => void;
  // The Activeren / Waarde toevoegen flow, rendered by the caller when the
  // operator may sell (it needs the WisePOS of the selected location).
  loadAction?: ReactNode;
  compact?: boolean;
}) {
  const { can } = useAuth();
  const { card, loads, transactions, holds, events } = detail;
  const st = giftCardStatusOf(card.display_status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdjust, setShowAdjust] = useState(false);

  async function toggleBlocked() {
    const blocking = card.blocked_at == null;
    const reason = blocking ? window.prompt('Waarom blokkeren? (bijv. verloren, gestolen)') : null;
    if (blocking && reason === null) return;
    setBusy(true);
    setError(null);
    try {
      await setGiftCardBlocked(card.id, blocking, reason);
      onChanged();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="giftcard-view">
      <div className="detail-header">
        <h1 className="voucher-code">{card.code_display}</h1>
        <div className="detail-pills">
          <span className={`voucher-pill ${st.className}`}>{st.label}</span>
        </div>
      </div>

      <div className="giftcard-balance">
        <div className="giftcard-balance-amount">{formatEuro(card.available_cents)}</div>
        <div className="muted">
          {card.status === 'inactive'
            ? 'Nog niet geactiveerd'
            : card.expires_at
              ? `Geldig t/m ${formatVenueDate(card.expires_at)}`
              : null}
          {card.held_cents > 0 ? ` · ${formatEuro(card.held_cents)} gereserveerd voor een lopende betaling` : null}
        </div>
      </div>

      {card.blocked_at ? (
        <div className="error-card">
          Geblokkeerd op {formatVenueDateTime(card.blocked_at)}
          {card.blocked_reason ? `: ${card.blocked_reason}` : ''}
        </div>
      ) : null}

      {can('booking_gift_card_sell') && !card.blocked_at && loadAction ? (
        <div className="giftcard-actions">{loadAction}</div>
      ) : null}

      {error ? <div className="error-card">{error}</div> : null}

      <div className="detail-grid">
        <section>
          <h2>Kaart</h2>
          <dl>
            <dt>Serienummer</dt><dd>{card.serial}</dd>
            <dt>Saldo</dt><dd>{formatEuro(card.balance_cents)}</dd>
            <dt>Geactiveerd</dt><dd>{card.activated_at ? formatVenueDateTime(card.activated_at) : '—'}</dd>
            <dt>Laatst opgewaardeerd</dt><dd>{card.last_loaded_at ? formatVenueDateTime(card.last_loaded_at) : '—'}</dd>
            <dt>Geldig t/m</dt><dd>{card.expires_at ? formatVenueDate(card.expires_at) : '—'}</dd>
            <dt>Batch</dt><dd>{card.batch_label ?? '—'}</dd>
          </dl>
          {can('booking_gift_card_manage') ? (
            <div className="giftcard-manage">
              <button className="btn-link" disabled={busy} onClick={() => void toggleBlocked()}>
                {card.blocked_at ? 'Deblokkeren' : 'Blokkeren'}
              </button>
              {card.status === 'active' ? (
                <button className="btn-link" disabled={busy} onClick={() => setShowAdjust(true)}>
                  Saldo corrigeren
                </button>
              ) : null}
            </div>
          ) : null}
        </section>

        <section>
          <h2>Transacties</h2>
          {transactions.length === 0 ? (
            <div className="muted">Nog geen transacties.</div>
          ) : (
            <table className="giftcard-tx-table">
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="muted">{formatVenueDateTime(t.created_at)}</td>
                    <td>
                      {TX_KIND_LABEL[t.kind]}
                      {t.booking_number ? (
                        <> · <Link to={`/booking/${t.booking_number}`}>{t.booking_number}</Link></>
                      ) : null}
                      {t.note ? <div className="muted">{t.note}</div> : null}
                    </td>
                    <td className={t.amount_cents < 0 ? 'giftcard-tx-out' : 'giftcard-tx-in'}>
                      {t.amount_cents < 0 ? '−' : '+'}{formatEuro(Math.abs(t.amount_cents))}
                    </td>
                    <td className="muted">{formatEuro(t.balance_after_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {!compact ? (
        <div className="detail-grid">
          <section>
            <h2>Opwaarderingen</h2>
            {loads.length === 0 ? (
              <div className="muted">Nog niet opgewaardeerd.</div>
            ) : (
              <table className="giftcard-tx-table">
                <tbody>
                  {loads.map((l) => (
                    <tr key={l.id}>
                      <td className="muted">{formatVenueDateTime(l.created_at)}</td>
                      <td>
                        {l.is_activation ? 'Activering' : 'Opwaardering'} · {LOAD_STATUS_LABEL[l.status]}
                        {l.failure_code ? <span className="muted"> ({l.failure_code})</span> : null}
                        <div className="muted">
                          {[l.location_name, l.operator_email].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td>{formatEuro(l.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          {holds.length > 0 ? (
            <section>
              <h2>Gebruikt bij boekingen</h2>
              <table className="giftcard-tx-table">
                <tbody>
                  {holds.map((h) => (
                    <tr key={h.id}>
                      <td className="muted">{formatVenueDateTime(h.created_at)}</td>
                      <td>
                        {h.booking_number ? (
                          <Link to={`/booking/${h.booking_number}`}>{h.booking_number}</Link>
                        ) : '—'}
                        <span className="muted">
                          {' · '}
                          {h.status === 'active' ? 'gereserveerd' : h.status === 'committed' ? 'besteed' : `vrijgegeven${h.release_reason ? ` (${h.release_reason})` : ''}`}
                          {h.channel === 'counter' ? ' · balie' : ''}
                        </span>
                      </td>
                      <td>{formatEuro(h.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
        </div>
      ) : null}

      {!compact ? (
        <section className="detail-history">
          <h2>Geschiedenis</h2>
          <AuditTimeline
            items={events.map((e) => ({
              id: e.id,
              created_at: e.created_at,
              actor_type: e.actor_type,
              actor_label: e.actor_label,
              action: e.action,
              value: e.metadata ?? e.to_value,
            }))}
          />
        </section>
      ) : null}

      {showAdjust ? (
        <AdjustModal
          cardId={card.id}
          balanceCents={card.balance_cents}
          onClose={(changed) => {
            setShowAdjust(false);
            if (changed) onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function AdjustModal({
  cardId,
  balanceCents,
  onClose,
}: {
  cardId: string;
  balanceCents: number;
  onClose: (changed: boolean) => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = Math.round(Number(amount.replace(',', '.')) * 100);
  const valid = Number.isFinite(cents) && cents !== 0 && balanceCents + cents >= 0 && reason.trim().length >= 3;

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal voucher-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Saldo corrigeren</h2></div>
          <button className="btn-link dep-modal-close" onClick={() => onClose(false)} aria-label="Sluiten">✕</button>
        </header>
        <div className="dep-modal-body">
          <form
            className="walkin-step"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!valid || busy) return;
              setBusy(true);
              setError(null);
              try {
                await adjustGiftCard(cardId, cents, reason.trim());
                onClose(true);
              } catch (err) {
                setError(formatError(err));
                setBusy(false);
              }
            }}
          >
            <p className="muted">Huidig saldo {formatEuro(balanceCents)}. Gebruik een min-teken om af te boeken.</p>
            <label className="walkin-field">
              <span>Bedrag (€)</span>
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="bijv. 10 of -5,50" autoFocus />
            </label>
            <label className="walkin-field">
              <span>Reden</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Verplicht, komt in de geschiedenis" />
            </label>
            {error ? <div className="error-card">{error}</div> : null}
            <button type="submit" className="walkin-primary-btn" disabled={!valid || busy}>
              {busy ? 'Opslaan…' : 'Corrigeren'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
